import { useMutation, type UseMutationOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { type DateTime } from 'luxon';

import { ApiClient } from './api.client.js';
import { type MonitoringHoursEvent, type MonitoringHoursRule } from '../models/index.js';


export const MonitoringHoursApiKeys = {
  Rules: 'MonitoringHoursRules',
  Events: 'MonitoringHoursEvents',
};

export class MonitoringHoursApi extends ApiClient {
  public static async getMonitoringHoursRules(patientId?: string): Promise<MonitoringHoursRule[]> {
    return this.http
      .get(`/monitoring-hours-rules`, {
        params: {
          patientId,
        },
      })
      .then((res) => res.data);
  }

  public static async getMonitoringHoursEventsFor(
    patientId?: string,
    startDate?: DateTime,
    endDate?: DateTime,
    signal?: AbortSignal,
  ): Promise<MonitoringHoursEvent[]> {
    if (!patientId) {
      return Promise.reject(new Error('[getMonitoringHoursEventsForPatient] Invalid PatientId'));
    }
    return this.http
      .get(`/monitoring-hours-events`, {
        params: {
          patientId,
          ...(startDate && { startDate: startDate.toISO() }),
          ...(startDate && endDate && { endDate: endDate.toISO() }),
        },
        signal,
      })
      .then((res) => res.data);
  }

  public static createRule(rule: MonitoringHoursRule): Promise<MonitoringHoursRule> {
    return this.http.post('/monitoring-hours-rules', rule).then((res) => res.data);
  }

  public static updateRule(rule: MonitoringHoursRule): Promise<MonitoringHoursRule> {
    return this.http.put(`/monitoring-hours-rules/${rule.id}`, rule).then((res) => res.data);
  }

  public static deleteRule(rule: MonitoringHoursRule): Promise<void> {
    return this.http
      .delete(`/monitoring-hours-rules/${rule.id}`, {
        params: {
          start: rule.start,
        },
      })
      .then((res) => res.data);
  }

  public static createEvent(event: MonitoringHoursEvent): Promise<MonitoringHoursEvent> {
    return this.http.post('/monitoring-hours-events', event).then((res) => res.data);
  }

  public static updateEvent(event: MonitoringHoursEvent): Promise<MonitoringHoursEvent> {
    return this.http.put(`/monitoring-hours-events/${event.id}`, event).then((res) => res.data);
  }

  public static deleteEvent(eventId: string): Promise<void> {
    return this.http.delete(`/monitoring-hours-events/${eventId}`).then((res) => res.data);
  }
}

export function useMonitoringHoursRules(patientId?: string, queryOptions?: UseQueryOptions<MonitoringHoursRule[]>) {
  return useQuery<MonitoringHoursRule[]>(
    [MonitoringHoursApiKeys.Rules, patientId],
    () => MonitoringHoursApi.getMonitoringHoursRules(patientId),
    queryOptions,
  );
}

export function useMonitoringHoursEvents(
  patientId?: string,
  startDate?: DateTime,
  endDate?: DateTime,
  queryOptions?: UseQueryOptions<MonitoringHoursEvent[]>,
) {
  return useQuery<MonitoringHoursEvent[]>(
    [MonitoringHoursApiKeys.Events, patientId, startDate?.toISO(), endDate?.toISO()],
    ({ signal }) => MonitoringHoursApi.getMonitoringHoursEventsFor(patientId, startDate, endDate, signal),
    queryOptions,
  );
}

export function useCreateMonitoringHourRule(
  mutationOptions?: UseMutationOptions<MonitoringHoursRule, unknown, MonitoringHoursRule>,
) {
  return useMutation<MonitoringHoursRule, unknown, MonitoringHoursRule>(
    [MonitoringHoursApiKeys.Rules],
    (rule: MonitoringHoursRule) => MonitoringHoursApi.createRule(rule),
    mutationOptions,
  );
}

export function useUpdateMonitoringHourRule(
  mutationOptions?: UseMutationOptions<MonitoringHoursRule, AxiosError, MonitoringHoursRule>,
) {
  return useMutation<MonitoringHoursRule, AxiosError, MonitoringHoursRule>(
    [MonitoringHoursApiKeys.Rules],
    (rule: MonitoringHoursRule) => MonitoringHoursApi.updateRule(rule),
    mutationOptions,
  );
}

export function useDeleteMonitoringHourRule(
  mutationOptions?: UseMutationOptions<void, AxiosError, MonitoringHoursRule>,
) {
  return useMutation<void, AxiosError, MonitoringHoursRule>(
    [MonitoringHoursApiKeys.Rules],
    (rule: MonitoringHoursRule) => MonitoringHoursApi.deleteRule(rule),
    mutationOptions,
  );
}

export function useCreateMonitoringHourEvent(
  mutationOptions?: UseMutationOptions<MonitoringHoursEvent, unknown, MonitoringHoursEvent>,
) {
  return useMutation<MonitoringHoursEvent, unknown, MonitoringHoursEvent>(
    [MonitoringHoursApiKeys.Events],
    (event: MonitoringHoursEvent) => MonitoringHoursApi.createEvent(event),
    mutationOptions,
  );
}

export function useUpdateMonitoringHourEvent(
  mutationOptions?: UseMutationOptions<MonitoringHoursEvent, AxiosError, MonitoringHoursEvent>,
) {
  return useMutation<MonitoringHoursEvent, AxiosError, MonitoringHoursEvent>(
    [MonitoringHoursApiKeys.Events],
    (event: MonitoringHoursEvent) => MonitoringHoursApi.updateEvent(event),
    mutationOptions,
  );
}

export function useDeleteMonitoringHourEvent(mutationOptions?: UseMutationOptions<void, AxiosError, string>) {
  return useMutation<void, AxiosError, string>(
    [MonitoringHoursApiKeys.Events],
    (eventId: string) => MonitoringHoursApi.deleteEvent(eventId),
    mutationOptions,
  );
}
